import React, {useImperativeHandle, useLayoutEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import utils from 'helpers/utils';
import {useComponentProps} from 'helpers/hooks/utils';
import {withMemo} from 'helpers/wrapper';
import StyledBlocker from 'components/atoms/Layout/Blocker/Blocker.styles';
import Box from 'components/atoms/Layout/Box/Box';

const Blocker = withMemo(React.forwardRef((props, ref) => {
  const {
    hole,
    interactive,
    ...innerProps
  } = useComponentProps(props, 'Blocker', {
    static: ['hole', 'interactive']
  });

  const innerRef = useRef(null);
  const shadeRef = useRef(null);
  const ringRef = useRef(null);

  useImperativeHandle(ref, () => innerRef.current);

  useLayoutEffect(() => {
    if (hole) {
      const blocker = innerRef.current;
      const shade = shadeRef.current;
      let frame = null;
      let rect = null;

      const track = () => {
        const bbox = hole.getBoundingClientRect();
        const corner = getComputedStyle(hole).borderTopLeftRadius;
        const radius = corner.endsWith('%') ? (Math.min(bbox.width, bbox.height) * parseFloat(corner)) / 100 : (parseFloat(corner) || 0);
        const next = `${bbox.top},${bbox.left},${bbox.right},${bbox.bottom},${radius},${window.innerWidth},${window.innerHeight}`;

        if (next !== rect) {
          rect = next;
          blocker.style.setProperty('--blocker-hole-top', `${bbox.top}px`);
          blocker.style.setProperty('--blocker-hole-left', `${bbox.left}px`);
          blocker.style.setProperty('--blocker-hole-right', `${bbox.right}px`);
          blocker.style.setProperty('--blocker-hole-bottom', `${bbox.bottom}px`);
          blocker.style.setProperty('--blocker-hole-radius', `${radius}px`);

          const ring = ringRef.current.getBoundingClientRect();
          const ringStyle = getComputedStyle(ringRef.current);
          const edge = parseFloat(ringStyle.borderTopWidth) || 0;
          const top = ring.top + edge;
          const left = ring.left + edge;
          const right = ring.right - edge;
          const bottom = ring.bottom - edge;
          const r = Math.max(0, Math.min((parseFloat(ringStyle.borderTopLeftRadius) || 0) - edge, (right - left) / 2, (bottom - top) / 2));

          shade.style.clipPath = `path(evenodd, "M0 0H${window.innerWidth}V${window.innerHeight}H0Z ` +
            `M${left + r} ${top}H${right - r}A${r} ${r} 0 0 1 ${right} ${top + r}V${bottom - r}` +
            `A${r} ${r} 0 0 1 ${right - r} ${bottom}H${left + r}A${r} ${r} 0 0 1 ${left} ${bottom - r}` +
            `V${top + r}A${r} ${r} 0 0 1 ${left + r} ${top}Z")`;
        }

        frame = window.requestAnimationFrame(track);
      };

      track();

      return () => {
        window.cancelAnimationFrame(frame);
        shade.style.removeProperty('clip-path');
      };
    }
  }, [hole]);

  innerProps.className = utils.flattenClassName(innerProps.className, {
    hole: Boolean(hole),
    interactive: Boolean(interactive)
  });

  return <StyledBlocker ref={innerRef} {...innerProps}>
    {hole ? <Box ref={shadeRef} className="Blocker-shade"/> : null}
    {hole ? <Box ref={ringRef} className="Blocker-ring"/> : null}
  </StyledBlocker>
}));

Blocker.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  hole: PropTypes.object,
  interactive: PropTypes.bool
};

export default Blocker;
